/**
 * Geometry helpers that turn a child's scribble into something the physics
 * solver can handle: smooth it, thin it out, wrap it in a convex hull, and
 * compute the mass properties of that hull.
 */

import type { Vec } from './vec.ts';
import { cross, dist, sub } from './vec.ts';

/** Chaikin corner-cutting: rounds off the jitter of a finger-drawn path. */
export function smoothPath(points: Vec[], iterations = 2): Vec[] {
  let pts = points;
  for (let it = 0; it < iterations; it++) {
    if (pts.length < 3) return pts;
    const out: Vec[] = [pts[0]];
    for (let i = 0; i < pts.length - 1; i++) {
      const a = pts[i];
      const b = pts[i + 1];
      out.push({ x: a.x * 0.75 + b.x * 0.25, y: a.y * 0.75 + b.y * 0.25 });
      out.push({ x: a.x * 0.25 + b.x * 0.75, y: a.y * 0.25 + b.y * 0.75 });
    }
    out.push(pts[pts.length - 1]);
    pts = out;
  }
  return pts;
}

/** Drop points closer together than `minDist` — pointer events oversample badly. */
export function decimate(points: Vec[], minDist = 2): Vec[] {
  if (points.length === 0) return [];
  const out: Vec[] = [points[0]];
  for (let i = 1; i < points.length; i++) {
    if (dist(points[i], out[out.length - 1]) >= minDist) out.push(points[i]);
  }
  if (out.length === 1 && points.length > 1) out.push(points[points.length - 1]);
  return out;
}

/** Andrew's monotone chain. Returns counter-clockwise hull in screen coords (y down). */
export function convexHull(points: Vec[]): Vec[] {
  if (points.length < 3) return points.slice();
  const pts = points
    .slice()
    .sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x))
    .filter((p, i, arr) => i === 0 || p.x !== arr[i - 1].x || p.y !== arr[i - 1].y);
  if (pts.length < 3) return pts;

  const build = (src: Vec[]): Vec[] => {
    const stack: Vec[] = [];
    for (const p of src) {
      while (stack.length >= 2 && cross(sub(stack[stack.length - 1], stack[stack.length - 2]), sub(p, stack[stack.length - 2])) <= 0) {
        stack.pop();
      }
      stack.push(p);
    }
    stack.pop();
    return stack;
  };

  const lower = build(pts);
  const upper = build(pts.slice().reverse());
  return lower.concat(upper);
}

/** Signed area x2. Positive when the winding matches `convexHull`'s output. */
function area2(poly: Vec[]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    a += cross(p, q);
  }
  return a;
}

export function polygonArea(poly: Vec[]): number {
  return Math.abs(area2(poly)) / 2;
}

export function polygonCentroid(poly: Vec[]): Vec {
  if (poly.length === 0) return { x: 0, y: 0 };
  if (poly.length < 3) {
    let sx = 0;
    let sy = 0;
    for (const p of poly) {
      sx += p.x;
      sy += p.y;
    }
    return { x: sx / poly.length, y: sy / poly.length };
  }
  const a2 = area2(poly);
  if (Math.abs(a2) < 1e-9) {
    let sx = 0;
    let sy = 0;
    for (const p of poly) {
      sx += p.x;
      sy += p.y;
    }
    return { x: sx / poly.length, y: sy / poly.length };
  }
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    const w = cross(p, q);
    cx += (p.x + q.x) * w;
    cy += (p.y + q.y) * w;
  }
  return { x: cx / (3 * a2), y: cy / (3 * a2) };
}

/** Second moment of area about the centroid, per unit density. */
export function polygonInertia(poly: Vec[], centroid: Vec): number {
  if (poly.length < 3) return 1;
  let num = 0;
  let den = 0;
  for (let i = 0; i < poly.length; i++) {
    const p = sub(poly[i], centroid);
    const q = sub(poly[(i + 1) % poly.length], centroid);
    const w = Math.abs(cross(p, q));
    num += w * (p.x * p.x + p.x * q.x + q.x * q.x + p.y * p.y + p.y * q.y + q.y * q.y);
    den += w;
  }
  // I/m = (1/6) * sum(w * (p.p + p.q + q.q)) / sum(w)
  return den < 1e-9 ? 1 : num / (6 * den);
}

/** Furthest distance from `centre` to any point — the broad-phase radius. */
export function boundingRadius(points: Vec[], centre: Vec): number {
  let r = 0;
  for (const p of points) r = Math.max(r, dist(p, centre));
  return r;
}

/** Axis-aligned bounds of a point cloud. */
export function bounds(points: Vec[]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

/** A regular polygon with `sides` corners, or a circle-ish blob when sides is large. */
export function regularPolygon(sides: number, radius: number, rotation = -Math.PI / 2): Vec[] {
  const n = Math.max(3, Math.round(sides));
  const out: Vec[] = [];
  for (let i = 0; i < n; i++) {
    const a = rotation + (i / n) * Math.PI * 2;
    out.push({ x: Math.cos(a) * radius, y: Math.sin(a) * radius });
  }
  return out;
}

/** Is `p` inside the convex polygon? Winding-agnostic. */
export function pointInConvex(poly: Vec[], p: Vec): boolean {
  if (poly.length < 3) return false;
  let pos = 0;
  let neg = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    const side = cross(sub(b, a), sub(p, a));
    if (side > 0) pos++;
    else if (side < 0) neg++;
    if (pos > 0 && neg > 0) return false;
  }
  return true;
}
